import { useEffect, useState, useCallback, useRef } from 'react';

export interface SplitPaneOptions {
  /** 侧边栏打开时的回调 */
  onOpen?: () => void;
  /** 侧边栏关闭时的回调 */
  onClose?: () => void;
}

/**
 * useSplitPane 侧边栏状态管理 Hook（V2）
 *
 * 支持在侧边栏打开/关闭时执行回调函数。
 *
 * @param defaultVisible - 默认显示状态
 * @param options - 可选配置（V2 新增：onOpen/onClose 回调）
 * @returns 侧边栏控制方法和状态
 *
 * @example
 * // V2 推荐用法（带回调）
 * ```tsx
 * const { visible, show, hide } = useSplitPane(false, {
 *   onOpen: () => collapseMenu(),
 *   onClose: () => expandMenu(),
 * });
 * ```
 *
 * @example
 * // 旧用法（已弃用，建议迁移到 V2）
 * ```tsx
 * const { visible, show, hide, toggle } = useSplitPane(false);
 * ```
 */
export function useSplitPane(defaultVisible?: boolean, options?: SplitPaneOptions): {
  visible: boolean;
  show: () => void;
  hide: () => void;
  toggle: () => void;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
};
/** @deprecated 请使用 `useSplitPane(defaultVisible, { onOpen, onClose })` 替代，利用回调机制管理关联状态 */
export function useSplitPane(defaultVisible = false, options?: SplitPaneOptions): any {
  const [visible, setVisible] = useState(defaultVisible);

  // 使用 ref 保存回调避免 show/hide 中产生 stale closure
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const show = useCallback(() => {
    setVisible(true);
    optionsRef.current?.onOpen?.();
  }, []);

  const hide = useCallback(() => {
    setVisible(false);
    optionsRef.current?.onClose?.();
  }, []);

  const toggle = useCallback(() => {
    setVisible((prev) => {
      const next = !prev;
      if (next) {
        optionsRef.current?.onOpen?.();
      } else {
        optionsRef.current?.onClose?.();
      }
      return next;
    });
  }, []);

  return {
    visible,
    show,
    hide,
    toggle,
    setVisible,
  };
}

/**
 * useSplitPaneKeyboard 侧边栏键盘交互 Hook
 *
 * 支持 ESC 键关闭侧边栏
 *
 * @param visible - 侧边栏显示状态
 * @param onClose - 关闭回调
 * @param enabled - 是否启用键盘交互，默认 true
 *
 * @example
 * ```tsx
 * useSplitPaneKeyboard(visible, () => setVisible(false));
 * ```
 */
export function useSplitPaneKeyboard(
  visible: boolean,
  onClose: () => void,
  enabled = true
) {
  useEffect(() => {
    if (!enabled || !visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, onClose, enabled]);
}

/**
 * useSplitPaneResponsive 侧边栏响应式 Hook
 *
 * 根据屏幕宽度自动调整侧边栏宽度
 *
 * @param defaultWidth - 默认宽度
 * @param breakpoints - 断点配置
 * @returns 当前侧边栏宽度
 *
 * @example
 * ```tsx
 * const sidebarWidth = useSplitPaneResponsive('368px', {
 *   sm: '100%',
 *   md: '400px',
 *   lg: '368px',
 * });
 * ```
 */
export function useSplitPaneResponsive(
  defaultWidth: string | number = '368px',
  breakpoints?: {
    sm?: string | number;  // < 768px
    md?: string | number;  // >= 768px && < 1024px
    lg?: string | number;  // >= 1024px
  }
) {
  const [width, setWidth] = useState(defaultWidth);

  useEffect(() => {
    if (!breakpoints) return;

    const updateWidth = () => {
      const screenWidth = window.innerWidth;

      if (screenWidth < 768 && breakpoints.sm) {
        setWidth(breakpoints.sm);
      } else if (screenWidth < 1024 && breakpoints.md) {
        setWidth(breakpoints.md);
      } else if (breakpoints.lg) {
        setWidth(breakpoints.lg);
      } else {
        setWidth(defaultWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [defaultWidth, breakpoints]);

  return width;
}

/**
 * useSplitPaneAnimation 侧边栏动画状态 Hook
 *
 * 用于监听动画结束事件
 *
 * @param visible - 侧边栏显示状态
 * @param duration - 动画时长（毫秒）
 * @param onOpen - 打开动画完成回调
 * @param onClose - 关闭动画完成回调
 *
 * @example
 * ```tsx
 * useSplitPaneAnimation(visible, 200, {
 *   onOpen: () => console.log('opened'),
 *   onClose: () => console.log('closed'),
 * });
 * ```
 */
export function useSplitPaneAnimation(
  visible: boolean,
  duration = 200,
  callbacks?: {
    onOpen?: () => void;
    onClose?: () => void;
  }
) {
  useEffect(() => {
    const timer = setTimeout(() => {
      if (visible && callbacks?.onOpen) {
        callbacks.onOpen();
      } else if (!visible && callbacks?.onClose) {
        callbacks.onClose();
      }
    }, duration);

    return () => clearTimeout(timer);
  }, [visible, duration, callbacks]);
}

/**
 * 默认配置常量
 */
export const SPLIT_PANE_DEFAULTS = {
  /** 默认侧边栏宽度 */
  SIDEBAR_WIDTH: '360px',
  /** 默认侧边栏间距 */
  SIDEBAR_GAP: '20px',
  /** 默认动画时长（毫秒） */
  ANIMATION_DURATION: 200,
  /** 事件详情侧边栏宽度 */
  EVENT_DETAIL_WIDTH: '368px',
} as const;

/**
 * 常用预设配置
 */
export const SPLIT_PANE_PRESETS = {
  /** 事件详情侧边栏 */
  eventDetail: {
    sidebarWidth: SPLIT_PANE_DEFAULTS.EVENT_DETAIL_WIDTH,
    animationDuration: SPLIT_PANE_DEFAULTS.ANIMATION_DURATION,
  },
  /** 标准侧边栏 */
  standard: {
    sidebarWidth: SPLIT_PANE_DEFAULTS.SIDEBAR_WIDTH,
    animationDuration: SPLIT_PANE_DEFAULTS.ANIMATION_DURATION,
  },
  /** 窄侧边栏 */
  narrow: {
    sidebarWidth: '280px',
    animationDuration: 150,
  },
  /** 宽侧边栏 */
  wide: {
    sidebarWidth: '480px',
    animationDuration: 250,
  },
} as const;
